import { Clock } from "lucide-react";
import { Business, Package } from "@/types/booking";
import { Button } from "./button";
import { useBooking } from "@/contexts/BookingContext";
import { formatPrice } from "@/lib/utils";

function VoucherCard({
  pkg,
  businessInfo,
  onPurchase,
  selected,
}: {
  pkg: Package;
  businessInfo: Business | null;
  onPurchase: () => void;
  selected: boolean;
}) {
  const { addToast } = useBooking();

  // Get the price (similar to PackageBookingCard logic)
  const price = pkg?.usesCustomPrice ? pkg?.customPrice : pkg?.totalPrice;

  // Helper function to limit string length
  const limitString = (str: string, limit: number) => {
    if (!str) return "";
    return str.length > limit ? str.substring(0, limit) + "..." : str;
  };

  // Convert package services to string
  const convertPackageServicesNamesToString = (packageServices: any[]) => {
    if (!packageServices || packageServices.length === 0) return "";
    return packageServices
      .map((ps: any) => `${ps.quantity}x ${ps.service?.name || ps.name}`)
      .join(", ");
  };

  return (
    <div className="w-full">
      <div
        className="relative bg-[url('https://assets.withsplice.com/app_images/20250828/voucher_cover.png')] w-full bg-cover bg-center bg-no-repeat h-[150px] cursor-pointer rounded-md overflow-hidden"
        onClick={onPurchase}
        style={{
          clipPath:
            "polygon(0 0, calc(100% - 15px) 0, 100% 15px, 100% calc(100% - 15px), calc(100% - 15px) 100%, 15px 100%, 0 calc(100% - 15px), 0 15px)",
        }}
      >
        {/* Ticket cutouts using pseudo-elements */}
        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-[15px] h-[30px] bg-white rounded-r-full -translate-x-[7.5px]"></div>
        <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-[15px] h-[30px] bg-white rounded-l-full translate-x-[7.5px]"></div>

        <div className="w-full h-full flex justify-between py-5 px-6">
          <div className="w-full max-w-[70%] flex flex-col justify-between">
            <div className="flex gap-2">
              <div className="w-[14px] h-[14px] flex justify-center items-center rounded bg-gray-50">
                <Clock className="w-[10px] h-[10px] text-gray-600" />
              </div>
              <p className="text-xs font-normal text-white uppercase">
                Valid for {pkg?.validityMonths || 1} Month
                {(pkg?.validityMonths || 1) > 1 ? "s" : ""}
              </p>
            </div>
            <div className="flex flex-col justify-start space-y-2">
              <p className="text-sm font-normal text-white">{pkg?.name}</p>
              <p className="text-xs font-normal text-white">
                {limitString(
                  convertPackageServicesNamesToString(
                    pkg?.packageServices || [],
                  ),
                  20,
                )}
              </p>
            </div>
            <p className="text-base font-semibold text-white">
              {businessInfo?.country?.currency[0]?.symbol || "₦"}
              {formatPrice(price, "").replace(/[^\d.,]/g, "")}
            </p>
          </div>
          <div className="flex flex-col justify-center items-center w-[50px] space-y-4">
            {/* Placeholder for logo - using a simple text logo since SVG components aren't available */}
            <div className="w-[25px] h-[35px] bg-white/20 rounded flex items-center justify-center">
              <span className="text-white text-xs font-bold">S</span>
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-xs font-normal text-white text-center">
                {businessInfo?.name}
              </p>
              <p className="text-[10px] font-normal text-gray-400 text-center">
                Powered by Splice
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Purchase button - positioned below the card with proper spacing */}
      <div className="mt-4">
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onPurchase();
          }}
          className={
            selected
              ? "w-full bg-green-300 text-white"
              : "w-full border-green-300 text-green-300 hover:bg-green-300 hover:text-white transition-colors"
          }
          variant={selected ? "default" : "outline"}
        >
          {selected ? "Selected" : "Purchase as Voucher"}
        </Button>
      </div>
    </div>
  );
}

export default VoucherCard;
